import {
    QueryClient,
    useMutation,
    UseMutationResult,
    useQuery,
    useQueryClient,
    UseQueryResult
} from '@tanstack/react-query';
import { UserModel } from 'src/types/user.model';
import UserService from 'src/services/user.service';
import AuthService, { SignupPayload } from 'src/services/auth.service';
import { invalidateEligibility } from 'src/hooks/queries/eligibility';
import { goToCurrentBudget, goToLandingPage } from 'src/services/router.service';
import { useHistory } from 'react-router-dom';
import Cookies from 'js-cookie';
import { SPENDSMART_TERMS_ACCEPTED_COOKIE_NAME } from 'src/utils/constants';

const userQueryKey: string = 'user';
const loginQueryKey: string = 'login';
const userAttributesQueryKey: string = 'userAttributes';

export const useUser = (): UseQueryResult<UserModel> => {
    return useQuery<UserModel>({
        queryKey: [userQueryKey],
        queryFn: async () => await UserService.getUser()
    });
};

export const useUserAttributes = (): UseQueryResult<{ [key: string]: any }> => {
    return useQuery<{ [key: string]: any }>({
        queryKey: [userAttributesQueryKey],
        queryFn: async () => await UserService.getAttributes()
    });
};

export const useUpdateUserAttributes = (): UseMutationResult<any, unknown, { [key: string]: any }> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (attributes: { [key: string]: any }) => await UserService.putAttributes(attributes),
        onMutate: async (attributes: { [key: string]: any }) => {
            // Cancel any outgoing refetches so they don't overwrite our optimistic update
            await queryClient.cancelQueries({ queryKey: [userQueryKey] });

            // Snapshot the previous value
            const previousUser: UserModel = queryClient.getQueryData([userQueryKey]);
            if (!previousUser) {
                return;
            }

            // Optimistically update to the new value
            queryClient.setQueryData([userQueryKey], () => {
                const previousUserCopy: UserModel = { ...previousUser };
                previousUserCopy.attributes = { ...previousUser.attributes, ...attributes };
                return previousUserCopy;
            });

            // Return a context object with the snapshotted value
            return { previousUser };
        },
        onError: (_err, _newUser, context) => {
            queryClient.setQueryData([userQueryKey], context.previousUser);
        },
        // Always refetch after error or success:
        onSettled: () => {
            void invalidateUser(queryClient);
            void invalidateUserAttributes(queryClient);
        }
    });
};

export const useDeleteUserAttribute = (): UseMutationResult<any, unknown, string> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (attribute: string) => await UserService.deleteAttribute(attribute),
        onMutate: async (attribute: string) => {
            // Cancel any outgoing refetches so they don't overwrite our optimistic update
            await queryClient.cancelQueries({ queryKey: [userQueryKey] });

            // Snapshot the previous value
            const previousUser: UserModel = queryClient.getQueryData([userQueryKey]);
            if (!previousUser) {
                return;
            }

            // Optimistically update to the new value
            queryClient.setQueryData([userQueryKey], () => {
                const previousUserCopy: UserModel = { ...previousUser };
                delete previousUserCopy.attributes[attribute];
                return previousUserCopy;
            });

            // Return a context object with the snapshotted value
            return { previousUser };
        },
        onError: (_err, _newUser, context) => {
            queryClient.setQueryData([userQueryKey], context.previousUser);
        },
        // Always refetch after error or success:
        onSettled: () => {
            void invalidateUser(queryClient);
            void invalidateUserAttributes(queryClient);
        }
    });
};

export const useSignupUser = (): UseMutationResult<any, unknown, SignupPayload> => {
    const queryClient = useQueryClient();
    const history = useHistory();
    return useMutation({
        mutationFn: async (signupPayload: SignupPayload) => await AuthService.signup(signupPayload),
        onSuccess: async (): Promise<void> => {
            await invalidateEligibility(queryClient);
            goToCurrentBudget(false, history);
        }
    });
};

export const useLoginUser = (enabled: boolean): UseQueryResult<any> => {
    return useQuery({
        queryKey: [loginQueryKey],
        queryFn: async () => await AuthService.login(),
        enabled,
        staleTime: Infinity,
        cacheTime: Infinity
    });
};

export const useUnenrollUser = (): UseMutationResult<any, unknown, UserModel> => {
    return useMutation({
        mutationFn: async (user: UserModel) => await UserService.unenrollUser(user),
        onSuccess: () => {
            Cookies.remove(SPENDSMART_TERMS_ACCEPTED_COOKIE_NAME);
            goToLandingPage();
        }
    });
};

export const invalidateUser = async (queryClient: QueryClient): Promise<void> => {
    await queryClient.invalidateQueries([userQueryKey]);
};

export const invalidateUserAttributes = async (queryClient: QueryClient): Promise<void> => {
    await queryClient.invalidateQueries([userAttributesQueryKey]);
};
